import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { BaseListComponent } from '../../organisms/base-list/base-list.component';
import { Colorset, colorsetTemplates } from '../../../models/colorset';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import {
  MenuItem,
  MenuItemCommandEvent,
  MessageService,
  SharedModule,
} from 'primeng/api';
import { SidebarService } from '../../../services/menu/sidebar.service';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { DebugComponent } from '../../atoms/debug/debug.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../../organisms/mask/mask.component';
import { NgIf } from '@angular/common';
import { SidebarModule } from 'primeng/sidebar';
import { TableCaptionComponent } from '../table-list-caption/table-list-caption.component';
import { TableModule } from 'primeng/table';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { ColorsComponent } from '../../atoms/colors/colors.component';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { ColorsetDefaultsComponent } from '../colorset-defaults/colorset-defaults.component';
import { ColorsetDefaultsService } from '../../../services/colorset/colorset-defaults.service';
import { Menu, MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { checkVisibleMenuItems } from '../../../utility/array.utility';
import {
  PillItem,
  PillListComponent,
} from '../../atoms/pill-list/pill-list.component';

@Component({
  selector: 'app-colorset-administration',
  standalone: true,
  imports: [
    DebugComponent,
    HeaderComponent,
    MaskComponent,
    NgIf,
    RouterOutlet,
    SharedModule,
    SidebarModule,
    TableCaptionComponent,
    TableModule,
    ColorsComponent,
    RouterLink,
    AlertComponent,
    ColorsetDefaultsComponent,
    PillListComponent,
    MenuModule,
    ButtonModule,
    TooltipModule,
  ],
  templateUrl: './colorset-administration.component.html',
  styleUrl: './colorset-administration.component.scss',
})
export class ColorsetAdministrationComponent
  extends BaseListComponent<Colorset>
  implements AfterViewInit
{
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-11',
    icon: 'fas fa-palette',
    headerText: 'Farbsets',
  };

  protected readonly colorsetTemplates = colorsetTemplates;
  readonly store = inject(ColorsetStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly defaultsService = inject(ColorsetDefaultsService);
  rowMenuItems: MenuItem[] = [];
  override menuItems: MenuItem[] = [];
  override sidebarId = 'colorset-administration';

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
  }

  ngAfterViewInit() {
    this.sidebarService.setConfiguration(this.sidebarConfig);
  }

  /**
   * "Standard für" slots of a colorset as pills (collapsed after 2 entries).
   */
  defaultPills(colorset: Colorset): PillItem[] {
    return this.defaultsService.usageOf(colorset._id).map(slot => ({
      id: slot.key,
      label: slot.label,
      icon: slot.icon,
    }));
  }

  /**
   * Builds and opens the row action menu for a colorset: edit, delete and the
   * "Standard für" slots (toggle - assigned slots are checked and get cleared
   * on click). Entries are hidden according to the user's rights.
   */
  openMenu(event: Event, colorset: Colorset, menu: Menu): void {
    event.stopPropagation();
    const canEdit = this.activeUserStore.canAccessEntityAdder();
    const canSetDefaults = this.activeUserStore.canAccessConfig();
    const defaults = this.defaultsService.defaults();

    // p-menu renders every top-level entry as a group header as soon as one
    // entry has sub items, so edit/delete live in a group of their own.
    this.rowMenuItems = checkVisibleMenuItems([
      {
        label: 'Farbset',
        visible: canEdit,
        items: [
          {
            label: 'Bearbeiten',
            icon: 'fas fa-pen',
            command: () =>
              this.router.navigate(
                [{ outlets: { bearbeiten: [colorset._id] } }],
                { relativeTo: this.route }
              ),
          },
          {
            label: 'Löschen',
            icon: 'fas fa-trash',
            command: (menuEvent: MenuItemCommandEvent) =>
              this.confirm(menuEvent.originalEvent, colorset, colorset.name),
          },
        ],
      },
      {
        label: 'Standard für',
        visible: canSetDefaults,
        items: this.defaultsService.slots.map(slot => {
          const active = defaults[slot.key] === colorset._id;
          return {
            label: slot.label,
            icon: active ? 'fas fa-check' : slot.icon,
            styleClass: active ? 'font-bold' : '',
            command: () =>
              this.defaultsService.setDefault(
                slot.key,
                active ? null : colorset._id || null
              ),
          };
        }),
      },
    ]);
    queueMicrotask(() => menu.toggle(event));
  }

  /**
   * Warns about default usages before deleting and clears them on accept.
   */
  override confirm(
    event: Event | undefined,
    colorset: Colorset,
    itemName: string
  ): void {
    const usage = this.defaultsService.usageOf(colorset._id);
    const message = usage.length
      ? `Möchten Sie <i>"${itemName}"</i> wirklich löschen?<br>` +
        `Es wird als Standard für <b>${usage.map(slot => slot.label).join(', ')}</b> verwendet; ` +
        'diese Standards werden zurückgesetzt.'
      : undefined;

    this.confirmationDialogService.confirmDeletion(
      event,
      itemName,
      this.store.entityLabel(),
      () => {
        if (colorset._id) this.defaultsService.clearUsagesOf(colorset._id);
        this.remove(colorset);
      },
      message
    );
  }
}
